import { type ReactNode, type RefObject, useContext, useId, useRef } from 'react';
import type { Placement } from 'react-aria';
import { useInteractOutside } from 'react-aria';
import { ButtonContext, ComboBoxStateContext } from 'react-aria-components';
import { createPortal } from 'react-dom';
import { useNodeDimensions } from '@gonfalon/dom/useNodeDimensions';
import { msg } from '@gonfalon/messages/msg';
import type { ListBoxProps } from '@launchpad-ui/components';
import {
  ComboBox,
  Group,
  IconButton,
  Input,
  ListBox,
  Popover,
  ProgressBar,
  TagGroup,
  TagList,
} from '@launchpad-ui/components';
import { clsx } from 'clsx';

import { RequiredLabel } from '../RequiredLabel/RequiredLabel';

import styles from './MultiComboBox.module.css';

export interface MultiComboBoxProps<T extends object> extends Partial<Omit<ListBoxProps<T>, 'renderEmptyState'>> {
  label?: ReactNode;
  description?: ReactNode;
  id?: string;
  placeholder?: string;
  className?: string;
  selectedKeys: string[];
  setFilterText?: (text: string) => void;
  filterText: string;
  isRequired?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  placement?: Placement;
  tags: () => ReactNode;
  onBlur?: () => void;
  onKeyDown?: (e: { key: string; defaultPrevented: boolean }) => void;
  renderEmptyState?: (inputValue: string) => ReactNode;
}

export function MultiComboBox<T extends object>(props: MultiComboBoxProps<T>) {
  const {
    label,
    description,
    placement = 'bottom end',
    id = 'search-items',
    placeholder = 'Select items',
    className,
    selectionMode,
    items,
    selectedKeys,
    setFilterText,
    filterText,
    isRequired,
    isClearable = true,
    isLoading,
    isDisabled,
    tags,
    onBlur,
    onKeyDown,
    onSelectionChange,
    renderEmptyState,
    onClick,
    children,
  } = props;
  const [node, dimensions] = useNodeDimensions<HTMLDivElement>();
  const tagGroupId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const showOptionsRef = useRef<HTMLButtonElement>(null);
  const isSingleSelect = selectionMode === 'single';
  const hasSelection = selectedKeys.length > 0;
  const showClearButton = isSingleSelect && hasSelection && isClearable && !isDisabled;

  return (
    <div ref={fieldRef} className={clsx(styles.multiComboBox, className)}>
      {hasSelection &&
        tagsRef.current &&
        createPortal(
          <TagGroup
            id={tagGroupId}
            aria-label={msg('ui.multiComboBox.selectedItemsLabel')}
            onRemove={
              isDisabled || !isClearable || isSingleSelect
                ? undefined
                : (selected) => {
                    const [, itemId] = String(selected.values().next().value).split(':');
                    onSelectionChange?.(new Set(selectedKeys.filter((item) => item !== itemId)));
                  }
            }
          >
            <TagList>{tags()}</TagList>
          </TagGroup>,
          tagsRef.current,
          tagGroupId,
        )}

      <ComboBox
        className={styles.comboBox}
        aria-label={msg('ui.multiComboBox.optionsLabel')}
        items={items}
        isDisabled={isDisabled}
        onSelectionChange={(key) => {
          if (key) {
            if (selectionMode === 'single') {
              onSelectionChange?.(new Set([String(key)]));
            } else {
              onSelectionChange?.(new Set(selectedKeys.concat(String(key))));
            }
          }
          setFilterText?.('');
        }}
        onClick={onClick}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.code === 'Escape') {
            setFilterText?.('');
            e.stopPropagation();
            onBlur?.();
          }
          onKeyDown?.(e);
        }}
        allowsEmptyCollection
      >
        <DismissOnPressOutside
          popoverRef={popoverRef}
          fieldRef={fieldRef}
          fallbackFocusRef={showOptionsRef}
          onDismiss={() => {
            setFilterText?.('');
            onBlur?.();
          }}
        />
        {label && (
          <RequiredLabel htmlFor={id} isRequired={isRequired}>
            {label}
          </RequiredLabel>
        )}
        {description && (
          <span className={styles.description} slot="description">
            {description}
          </span>
        )}
        <Group
          ref={node}
          className={clsx(styles.group, {
            [styles.singleValue]: isSingleSelect && hasSelection,
            [styles.withClearButton]: showClearButton,
          })}
          aria-label={msg('ui.multiComboBox.searchAndFilterLabel')}
        >
          <span className={styles.container} ref={tagsRef} />
          <Input
            ref={inputRef}
            id={id}
            aria-label={msg('ui.multiComboBox.searchLabel')}
            placeholder={hasSelection ? '' : placeholder}
            onKeyDownCapture={(e) => {
              if (e.code === 'Backspace' && filterText.length === 0 && isClearable) {
                if (isSingleSelect) {
                  onSelectionChange?.(new Set());
                } else {
                  onSelectionChange?.(new Set(selectedKeys.slice(0, -1)));
                }
              }
            }}
            value={filterText}
            onChange={({ target: { value } }) => setFilterText?.(value)}
          />
          {isLoading && <ProgressBar aria-label={msg('ui.status.loading')} isIndeterminate size="small" />}
          {showClearButton && (
            <ButtonContext.Provider value={{}}>
              <IconButton
                className={styles.clearButton}
                aria-label={msg('ui.multiComboBox.clearSelectionLabel')}
                icon="cancel-circle-outline"
                size="small"
                variant="minimal"
                onPress={() => {
                  onSelectionChange?.(new Set());
                  setFilterText?.('');
                  inputRef.current?.focus();
                }}
              />
            </ButtonContext.Provider>
          )}
          <IconButton
            ref={showOptionsRef}
            aria-label={msg('ui.multiComboBox.showOptionsLabel')}
            icon="chevron-down"
            size="small"
            variant="minimal"
          />
        </Group>
        <Popover ref={popoverRef} maxHeight={320} placement={placement} triggerRef={node} shouldFlip={false} isNonModal>
          <ListBox<T>
            className={styles.listBox}
            aria-label={msg('ui.multiComboBox.optionsLabel')}
            escapeKeyBehavior="none"
            selectionMode={selectionMode}
            style={{ minWidth: dimensions.width ? dimensions.width - 20 : undefined }}
            renderEmptyState={() => renderEmptyState?.(filterText) ?? null}
          >
            {children}
          </ListBox>
        </Popover>
      </ComboBox>
    </div>
  );
}

/**
 * Closes the option list on a press outside the field, which a non-modal popover does not do on its own.
 * Focus moves to `fallbackFocusRef` so a dialog's focus containment cannot restore it to the input, which reopens the list.
 */
function DismissOnPressOutside({
  popoverRef,
  fieldRef,
  fallbackFocusRef,
  onDismiss,
}: {
  popoverRef: RefObject<HTMLElement | null>;
  fieldRef: RefObject<HTMLElement | null>;
  fallbackFocusRef: RefObject<HTMLElement | null>;
  onDismiss: () => void;
}) {
  const state = useContext(ComboBoxStateContext);

  const dismiss = (event: PointerEvent | MouseEvent | TouchEvent) => {
    if (!state?.isOpen || (event.target instanceof Node && fieldRef.current?.contains(event.target))) {
      return;
    }

    fallbackFocusRef.current?.focus();
    state.close();
    onDismiss();
  };

  useInteractOutside({
    ref: popoverRef,
    isDisabled: !state?.isOpen,
    onInteractOutsideStart: dismiss,
    onInteractOutside: dismiss,
  });

  return null;
}
